import {
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  FormHelperText,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Controller, DeepPartial, useFormContext } from 'react-hook-form'
import { FileUpload } from 'components/FileUpload'
import { RequiredStar } from 'components/RequiredStarSpan'
import { ILot } from 'types/lots'
import { useCurrentProcurementPlansQuery } from 'services/procurementPlans'
import { useEffect, useState } from 'react'

type Props = {
  lot: ILot
  isSubmissionTimePassed: boolean
}

export const GeneralLotInformation = ({ lot, isSubmissionTimePassed }: Props) => {
  const { t } = useTranslation()
  const {
    control,
    formState: { errors },
  } = useFormContext<DeepPartial<ILot>>()
  const [isDisabled, setIsDisabled] = useState(false)

  useEffect(() => {
    setIsDisabled(isSubmissionTimePassed)
  }, [isSubmissionTimePassed])

  const { data: procurementPlans } = useCurrentProcurementPlansQuery()

  return (
    <Stack spacing={2} width="60%">
      <Controller
        control={control}
        name="id"
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            disabled
            label={t('lot-number')}
            variant="outlined"
          />
        )}
      />
      <Controller
        control={control}
        name="name"
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            disabled
            label={t('lot-name')}
            variant="outlined"
          />
        )}
      />
      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense" required>
        <Controller
          name="procurementPlanItemId"
          control={control}
          rules={{ required: t('required') }}
          render={({ field }) => (
            <>
              <InputLabel id="annual-plan-helper-label">
                {t('annual-procurement-plan')} <RequiredStar>*</RequiredStar>
              </InputLabel>

              <Select
                {...field}
                variant="outlined"
                error={!!errors.procurementPlanItemId}
                sx={{ background: 'white' }}
                labelId="annual-plan-helper-label"
                label={
                  <>
                    {t('annual-procurement-plan')} <RequiredStar>*</RequiredStar>
                  </>
                }
                disabled={isDisabled}
              >
                {procurementPlans?.procurementPlanItems?.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        />
        {errors.procurementPlanItemId && (
          <FormHelperText error>
            {errors.procurementPlanItemId?.message}
          </FormHelperText>
        )}
      </FormControl>

      <Stack direction="row" spacing={1}>
        <FormControl fullWidth margin="dense">
          <Controller
            name="estimatedPriceAmount"
            control={control}
            rules={{ required: t('required') }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                error={!!errors.estimatedPriceAmount}
                type="number"
                label={
                  <>
                    {t('predicted-price')} <RequiredStar>*</RequiredStar>
                  </>
                }
                variant="outlined"
                disabled={isDisabled}
              />
            )}
          />
          {errors.estimatedPriceAmount && (
            <FormHelperText error>
              {errors.estimatedPriceAmount?.message}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel id="currency-select-helper-label">{t('currency')}</InputLabel>
          <Select
            variant="outlined"
            sx={{ background: 'white' }}
            labelId="currency-select-helper-label"
            disabled
            value="AZN"
            defaultValue="AZN"
            label={<>{t('currency')}</>}
          >
            <MenuItem value="AZN">AZN</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Stack direction="row" spacing={1}>
        <FormControl fullWidth margin="dense">
          <Controller
            name="participationFeeAmount"
            control={control}
            rules={{ required: t('required') }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                error={!!errors.participationFeeAmount}
                type="number"
                label={
                  <>
                    {t('participation-fee')} (AZN)
                    <RequiredStar>*</RequiredStar>
                  </>
                }
                variant="outlined"
                disabled={isDisabled}
              />
            )}
          />
          {errors.participationFeeAmount && (
            <FormHelperText error>
              {errors.participationFeeAmount?.message}
            </FormHelperText>
          )}
        </FormControl>

        <FormControl fullWidth margin="dense">
          <InputLabel id="currency-select-helper-label">{t('currency')}</InputLabel>

          <Select
            variant="outlined"
            sx={{ background: 'white' }}
            labelId="currency-select-helper-label"
            disabled
            value="AZN"
            defaultValue="AZN"
            label={<>{t('currency')}</>}
          >
            <MenuItem value="AZN">AZN</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Controller
        name="deliveryAddress"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label={t('delivery-address')}
            variant="outlined"
            disabled={isDisabled}
          />
        )}
      />

      <Stack direction="row" spacing={1}>
        <FormControl fullWidth margin="dense">
          <Controller
            name="deliveryDurationDays"
            control={control}
            rules={{ required: t('required') }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                error={!!errors.deliveryDurationDays}
                label={
                  <>
                    {t('delivery-time')} <RequiredStar>*</RequiredStar>
                  </>
                }
                variant="outlined"
                disabled={isDisabled}
              />
            )}
          />
          {errors.deliveryDurationDays && (
            <FormHelperText error>
              {errors.deliveryDurationDays?.message}
            </FormHelperText>
          )}
        </FormControl>

        <FormControl fullWidth margin="dense">
          <Controller
            name="deliveryTerms"
            control={control}
            rules={{ required: t('required') }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                error={!!errors.deliveryTerms}
                label={
                  <>
                    {t('delivery-terms')} <RequiredStar>*</RequiredStar>
                  </>
                }
                variant="outlined"
                disabled={isDisabled}
              />
            )}
          />
          {errors.deliveryTerms && (
            <FormHelperText error>{errors.deliveryTerms?.message}</FormHelperText>
          )}
        </FormControl>
      </Stack>

      <Controller
        name="paymentTerms"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label={t('payment-terms')}
            variant="outlined"
            disabled={isDisabled}
          />
        )}
      />

      <Controller
        name="termsFileId"
        control={control}
        render={({ field }) => (
          <FileUpload
            text={t('conditions')}
            handleFile={(file) => field.onChange(file.id)}
            fileId={field.value}
            accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            isDisabled={isDisabled}
          />
        )}
      />

      <Controller
        name="contractTemplateFileId"
        control={control}
        render={({ field }) => (
          <FileUpload
            text={t('contract-type')}
            handleFile={(file) => field.onChange(file.id)}
            fileId={field.value}
            isDisabled={isDisabled}
          />
        )}
      />

      <Controller
        control={control}
        name="extraInfo"
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label={t('extra-info')}
            multiline
            minRows={4}
            variant="outlined"
            disabled={isDisabled}
          />
        )}
      />

      <Controller
        name="extraFileId"
        control={control}
        render={({ field }) => (
          <FileUpload
            text={t('extra-document')}
            handleFile={(file) => field.onChange(file.id)}
            fileId={field.value}
            isDisabled={isDisabled}
          />
        )}
      />
    </Stack>
  )
}
